import { Box, Button, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import styles from "./PaymentAmountSelector.module.css";
import { Vocabulary } from "../../Utils/Vocabulary";

interface PaymentAmountSelectorProps {
  amountLeftToPay: number;
  onAmountSelect: (amount: number | null) => void;
}

const FIXED_AMOUNTS = [200, 500, 1000, 1500];

interface SelectorState {
  selectedAmount: number | null;
  customAmount: string;
  error: string;
}

export default function PaymentAmountSelector({
  amountLeftToPay,
  onAmountSelect,
}: PaymentAmountSelectorProps) {
  const [state, setState] = useState<SelectorState>({
    selectedAmount: null,
    customAmount: "",
    error: "",
  });

  const amountToDisplay =
    state.selectedAmount !== null
      ? state.selectedAmount
      : state.customAmount
      ? parseFloat(state.customAmount)
      : null;

  const handleFixedAmountClick = (amount: number) => {
    if (amount <= amountLeftToPay) {
      setState({
        selectedAmount: amount,
        customAmount: "",
        error: "",
      });
      onAmountSelect(amount);
    }
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    let error = "";

    if (value) {
      const amount = parseFloat(value);
      if (isNaN(amount) || amount <= 0) {
        error = Vocabulary.amountMustBePositive;
      } else if (amount > amountLeftToPay) {
        error = `${Vocabulary.amountCannotExceed} ${amountLeftToPay.toFixed(2)} RON`;
      }
    }

    setState({
      selectedAmount: null,
      customAmount: value,
      error,
    });

    if (value && !error) {
      const amount = parseFloat(value);
      onAmountSelect(amount);
    } else {
      onAmountSelect(null);
    }
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.amountInfo}>
        <Typography className={styles.label}>
          {Vocabulary.remainingAmountToPay}:
        </Typography>
        <Typography className={styles.amountLeft}>
          {amountLeftToPay.toFixed(2)} RON
        </Typography>
      </Box>

      <Box className={styles.fixedAmountsSection}>
        <Typography className={styles.sectionTitle}>
          {Vocabulary.selectFixedAmount}:
        </Typography>
        <Box className={styles.fixedAmountsGrid}>
          {FIXED_AMOUNTS.map((amount) => {
            const isDisabled = amount > amountLeftToPay;
            return (
              <Tooltip
                key={amount}
                title={
                  isDisabled
                    ? `${Vocabulary.amountCannotExceed} ${amountLeftToPay.toFixed(2)} RON`
                    : ""
                }
                placement="top"
              >
                <span>
                  <Button
                    variant={
                      state.selectedAmount === amount ? "contained" : "outlined"
                    }
                    color="primary"
                    fullWidth
                    onClick={() => handleFixedAmountClick(amount)}
                    disabled={isDisabled}
                    className={styles.fixedAmountButton}
                  >
                    {amount} RON
                  </Button>
                </span>
              </Tooltip>
            );
          })}
        </Box>
      </Box>

      <Box className={styles.divider}>
        <Typography className={styles.orText}>{Vocabulary.orText}</Typography>
      </Box>

      <Box className={styles.customAmountSection}>
        <TextField
          label={Vocabulary.enterCustomAmount}
          type="number"
          value={state.customAmount}
          onChange={handleCustomAmountChange}
          placeholder="0"
          fullWidth
          inputProps={{ min: 0, step: 1 }}
          className={styles.customInput}
          variant="outlined"
          error={!!state.error}
          helperText={state.error}
          InputProps={{
            endAdornment: <InputAdornment position="end">RON</InputAdornment>,
          }}
        />
      </Box>

      {amountToDisplay !== null && (
        <Box className={styles.summary}>
          <Typography className={styles.summaryLabel}>
            {Vocabulary.selectedAmount}:
          </Typography>
          <Typography className={styles.summaryAmount}>
            {amountToDisplay.toFixed(2)} RON
          </Typography>
        </Box>
      )}
    </Box>
  );
}
